var _DraggableItemList;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import memoize from 'memoizee';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { vsGripper } from '@deephaven/icons';
import { RangeUtils } from '@deephaven/utils';
import ItemList from "./ItemList.js";
import { Tooltip } from "./popper/index.js";
import "./DraggableItemList.css";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Show a draggable item list. It _must_ be used within a `DragDropContext`.
 * This implementation uses @hello-pangea/dnd for handling dragging and dropping of items.
 * We use ItemList to handle selection for multi drag and drop (not built in to @hello-pangea/dnd).
 * One caveat with the use of @hello-pangea/dnd is that it doesn't allow a drag to be initiated while
 * using a modifier key: https://github.com/atlassian/@hello-pangea/dnd/issues/1678
 */
class DraggableItemList extends PureComponent {
  static renderHandle() {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        children: "Drag to re-order"
      }), /*#__PURE__*/_jsx(FontAwesomeIcon, {
        icon: vsGripper
      })]
    });
  }
  static renderBadge(_ref) {
    var text = _ref.text;
    return text != null && text.length > 0 ? /*#__PURE__*/_jsx("span", {
      className: "number-badge",
      children: text
    }) : null;
  }
  static renderTextItem(_ref2) {
    var text = _ref2.text,
      _ref2$badgeText = _ref2.badgeText,
      badgeText = _ref2$badgeText === void 0 ? '' : _ref2$badgeText,
      _ref2$className = _ref2.className,
      className = _ref2$className === void 0 ? '' : _ref2$className;
    return /*#__PURE__*/_jsxs("div", {
      className: classNames('item-list-item-content', 'draggable-item-list-item-content', className),
      children: [/*#__PURE__*/_jsx("span", {
        className: "title",
        children: text
      }), DraggableItemList.renderBadge({
        text: badgeText
      }), DraggableItemList.renderHandle()]
    });
  }
  static renderItem(_ref3) {
    var _ref4, _item$displayValue;
    var item = _ref3.item,
      isClone = _ref3.isClone,
      selectedCount = _ref3.selectedCount;
    var text = item != null ? (_ref4 = (_item$displayValue = item.displayValue) !== null && _item$displayValue !== void 0 ? _item$displayValue : item.value) !== null && _ref4 !== void 0 ? _ref4 : "".concat(item) : '';
    var badgeText = isClone !== undefined && isClone ? "".concat(selectedCount) : '';
    var className = isClone !== undefined && isClone ? 'item-list-item-clone' : '';
    return DraggableItemList.renderTextItem({
      text,
      badgeText,
      className
    });
  }
  static getDraggableId(draggablePrefix, itemIndex) {
    return "".concat(draggablePrefix, "/").concat(itemIndex);
  }
  static getDraggableIndex(draggableId) {
    var num = draggableId.split('/').pop();
    return parseInt(num !== undefined ? num : '', 10);
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "itemList", void 0);
    _defineProperty(this, "getCachedDraggableItem", memoize((draggablePrefix, renderItem, item, itemIndex, isFocused, isSelected, isDragDisabled, style) => /*#__PURE__*/_jsx(Draggable, {
      draggableId: DraggableItemList.getDraggableId(draggablePrefix, itemIndex),
      index: itemIndex,
      isDragDisabled: isDragDisabled,
      children: provided => /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread(_objectSpread({
        role: "menuitem",
        className: "draggable-item-list-item",
        ref: provided.innerRef,
        tabIndex: -1
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, provided.draggableProps), provided.dragHandleProps), {}, {
        children: renderItem({
          item,
          itemIndex,
          isFocused,
          isSelected,
          style,
          isClone: false
        })
      }))
    }, itemIndex), {
      max: ItemList.CACHE_SIZE
    }));
    _defineProperty(this, "getCachedRenderDraggableItem", memoize((draggablePrefix, isDragDisabled, renderItem) => _ref5 => {
      var item = _ref5.item,
        itemIndex = _ref5.itemIndex,
        isFocused = _ref5.isFocused,
        isSelected = _ref5.isSelected,
        style = _ref5.style;
      return this.getCachedDraggableItem(draggablePrefix, renderItem, item, itemIndex, isFocused, isSelected, isDragDisabled, style);
    }, {
      max: 1
    }));
    _defineProperty(this, "getCachedRenderClone", memoize((draggingItemClassName, items, offset, renderItem) =>
    // eslint-disable-next-line react/no-unstable-nested-components, react/display-name, react/function-component-definition
    (provided, snapshot, rubric) => {
      // eslint-disable-next-line react/no-this-in-sfc
      var selectedCount = this.state.selectedCount;
      var draggableProps = provided.draggableProps,
        dragHandleProps = provided.dragHandleProps,
        innerRef = provided.innerRef;
      var itemIndex = rubric.source.index;
      var item = items[itemIndex - offset];
      return /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread(_objectSpread({
        className: classNames('draggable-item-list-dragging-item-container', draggingItemClassName)
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, draggableProps), dragHandleProps), {}, {
        ref: innerRef,
        children: /*#__PURE__*/_jsx("div", {
          className: classNames('draggable-item-list-dragging-item', {
            'two-dragged': selectedCount === 2
          }, {
            'multiple-dragged': selectedCount > 2
          }),
          children: renderItem({
            item,
            itemIndex,
            isFocused: false,
            isSelected: true,
            style: {},
            isClone: true,
            selectedCount
          })
        })
      }));
    }, {
      max: 1
    }));
    this.handleSelectionChange = this.handleSelectionChange.bind(this);
    this.itemList = /*#__PURE__*/React.createRef();
    this.state = {
      selectedCount: 0
    };
  }
  selectItem(itemIndex) {
    var _this$itemList$curren;
    (_this$itemList$curren = this.itemList.current) === null || _this$itemList$curren === void 0 || _this$itemList$curren.selectItem(itemIndex);
  }
  focusItem(itemIndex) {
    var _this$itemList$curren2;
    (_this$itemList$curren2 = this.itemList.current) === null || _this$itemList$curren2 === void 0 || _this$itemList$curren2.focusItem(itemIndex);
  }
  scrollToItem(itemIndex) {
    var _this$itemList$curren3;
    (_this$itemList$curren3 = this.itemList.current) === null || _this$itemList$curren3 === void 0 || _this$itemList$curren3.scrollToItem(itemIndex);
  }
  handleSelectionChange(selectedRanges) {
    this.setState({
      selectedCount: RangeUtils.count(selectedRanges)
    });
    var onSelectionChange = this.props.onSelectionChange;
    onSelectionChange(selectedRanges);
  }
  render() {
    var _this$props = this.props,
      className = _this$props.className,
      draggablePrefix = _this$props.draggablePrefix,
      draggingItemClassName = _this$props.draggingItemClassName,
      droppableId = _this$props.droppableId,
      isDoubleClickSelect = _this$props.isDoubleClickSelect,
      isDragDisabled = _this$props.isDragDisabled,
      isDropDisabled = _this$props.isDropDisabled,
      hasPlaceholder = _this$props.hasPlaceholder,
      isMultiSelect = _this$props.isMultiSelect,
      isStickyBottom = _this$props.isStickyBottom,
      itemCount = _this$props.itemCount,
      items = _this$props.items,
      offset = _this$props.offset,
      onFocusChange = _this$props.onFocusChange,
      onSelect = _this$props.onSelect,
      onViewportChange = _this$props.onViewportChange,
      renderItem = _this$props.renderItem,
      rowHeight = _this$props.rowHeight,
      selectedRanges = _this$props.selectedRanges,
      style = _this$props.style,
      dataTestId = _this$props['data-testid'];
    return /*#__PURE__*/_jsx(Droppable, {
      isDropDisabled: isDropDisabled,
      droppableId: droppableId,
      mode: "virtual",
      renderClone: this.getCachedRenderClone(draggingItemClassName, items, offset, renderItem),
      "data-testid": dataTestId,
      children: (provided, snapshot) => /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
        role: "menu",
        className: classNames('draggable-item-list', className, {
          'is-drop-disabled': isDropDisabled,
          'is-drag-disabled': isDragDisabled,
          'is-dragging-from-this': snapshot.draggingFromThisWith,
          'is-dragging-over': snapshot.isDraggingOver,
          'is-dropping': snapshot.draggingOverWith
        }),
        style: style,
        ref: provided.innerRef
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, provided.droppableProps), {}, {
        children: [/*#__PURE__*/_jsx(ItemList, {
          focusSelector: ".draggable-item-list-item",
          isDoubleClickSelect: isDoubleClickSelect,
          isDragSelect: false,
          isMultiSelect: isMultiSelect,
          isStickyBottom: isStickyBottom,
          itemCount: itemCount,
          items: items,
          onFocusChange: onFocusChange,
          onSelect: onSelect,
          onSelectionChange: this.handleSelectionChange,
          onViewportChange: onViewportChange,
          offset: offset,
          ref: this.itemList,
          renderItem: this.getCachedRenderDraggableItem(draggablePrefix, isDragDisabled, renderItem),
          rowHeight: rowHeight,
          selectedRanges: selectedRanges
        }), hasPlaceholder && provided.placeholder]
      }))
    });
  }
}
_DraggableItemList = DraggableItemList;
_defineProperty(DraggableItemList, "DEFAULT_ROW_HEIGHT", 30);
_defineProperty(DraggableItemList, "defaultProps", {
  className: '',
  draggingItemClassName: '',
  offset: 0,
  items: [],
  rowHeight: _DraggableItemList.DEFAULT_ROW_HEIGHT,
  isDeselectOnClick: true,
  isDoubleClickSelect: true,
  isDropDisabled: false,
  hasPlaceholder: false,
  isDragDisabled: false,
  isMultiSelect: false,
  isStickyBottom: false,
  disableSelect: false,
  style: null,
  onFocusChange() {
    // no-op
  },
  onSelect() {
    // no-op
  },
  onSelectionChange() {
    // no-op
  },
  onViewportChange() {
    // no-op
  },
  renderItem: _DraggableItemList.renderItem,
  selectedRanges: [],
  draggablePrefix: 'draggable-item',
  droppableId: 'droppable-item-list',
  'data-testid': undefined
});
export default DraggableItemList;
//# sourceMappingURL=DraggableItemList.js.map